import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { 
  Zap, Users, Building2, ShoppingCart, Save, RotateCcw, 
  TrendingUp, AlertTriangle, Check, Info, PieChart, 
  ArrowRight, Package, History
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

export const QueryManagementPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  
  // Data states
  const [distribution, setDistribution] = useState(null);
  const [departmentQuotas, setDepartmentQuotas] = useState([]);
  const [addons, setAddons] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [editedQuotas, setEditedQuotas] = useState({});
  
  useEffect(() => {
    fetchAllData();
    
    // Check for success/cancel params from Stripe
    if (searchParams.get('addon_success') === 'true') {
      toast.success('Query add-on purchased successfully!');
      // Remove params from URL
      navigate('/suite/docs/query-management', { replace: true });
    } else if (searchParams.get('addon_cancelled') === 'true') {
      toast.info('Add-on purchase was cancelled');
      navigate('/suite/docs/query-management', { replace: true });
    }
  }, []);
  
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [distRes, quotasRes, addonsRes, purchasesRes] = await Promise.all([
        axios.get(`${API}/admin/query-distribution`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/department-quotas`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscription/addons`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscription/addon-purchases`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setDistribution(distRes.data);
      setDepartmentQuotas(quotasRes.data.quotas || []);
      setAddons(addonsRes.data.addons || []);
      setPurchases(purchasesRes.data.purchases || []);
      
      // Initialize edited quotas
      const initial = {};
      (quotasRes.data.quotas || []).forEach(q => {
        initial[q.department] = q.quota_percentage;
      });
      setEditedQuotas(initial);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load query management data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleQuotaChange = (department, value) => {
    setEditedQuotas(prev => ({
      ...prev,
      [department]: value
    }));
  };
  
  const getTotalPercentage = () => {
    return Object.values(editedQuotas).reduce((sum, val) => sum + val, 0);
  };
  
  const handleSaveQuotas = async () => {
    const total = getTotalPercentage();
    if (total > 100) {
      toast.error(`Total quota (${total.toFixed(1)}%) exceeds 100%. Please adjust.`);
      return;
    }
    
    setSaving(true);
    try {
      const quotas = Object.entries(editedQuotas).map(([department, quota_percentage]) => ({
        department,
        quota_percentage,
        priority: departmentQuotas.find(q => q.department === department)?.priority || 1
      }));
      
      await axios.post(`${API}/admin/department-quotas/bulk`, { quotas }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Department quotas updated successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quotas');
    } finally {
      setSaving(false);
    }
  };
  
  const handleResetQuotas = () => {
    const reset = {};
    departmentQuotas.forEach(q => {
      reset[q.department] = q.quota_percentage;
    });
    setEditedQuotas(reset);
    toast.info('Quotas reset to current values');
  };
  
  const handlePurchaseAddon = async (addonId) => {
    if (addons.find(a => a.id === addonId)?.contact_sales) {
      navigate('/contact', { state: { inquiry: 'custom-queries' } });
      return;
    }
    
    setPurchasing(addonId);
    try {
      const response = await axios.post(`${API}/subscription/purchase-addon`, {
        addon_type: addonId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setPurchasing(null);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  const totalPercentage = getTotalPercentage();
  const isOverBudget = totalPercentage > 100;
  
  return (
    <div className="space-y-6 p-6" data-testid="query-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Query Management</h1>
          <p className="text-slate-400">Manage AI query distribution and purchase add-ons</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/suite/docs/billing')}>
          <ArrowRight className="w-4 h-4 mr-2" />
          Back to Billing
        </Button>
      </div>
      
      {/* Smart Distribution Overview */}
      <Card className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 border-indigo-500/30">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-400" />
            Smart Query Distribution
          </CardTitle>
          <CardDescription className="text-slate-300">
            Queries are automatically distributed based on your active users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-slate-900/50">
              <div className="text-sm text-slate-400">Total Queries</div>
              <div className="text-2xl font-bold text-white">
                {distribution?.total_queries?.toLocaleString() || 0}
                {distribution?.addon_queries > 0 && (
                  <span className="text-sm text-green-400 ml-2">
                    (+{distribution.addon_queries.toLocaleString()} add-on)
                  </span>
                )}
              </div>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50">
              <div className="text-sm text-slate-400">Active Users</div>
              <div className="text-2xl font-bold text-white">
                {distribution?.active_users || 0}
                <span className="text-sm text-slate-400 ml-1">
                  / {distribution?.plan_max_users || 0}
                </span>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50">
              <div className="text-sm text-slate-400">Per User Allocation</div>
              <div className="text-2xl font-bold text-emerald-400">
                ~{Math.round(distribution?.effective_per_user || 0)}
                <span className="text-sm text-slate-400 ml-1">/month</span>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50">
              <div className="text-sm text-slate-400">Used This Month</div>
              <div className="text-2xl font-bold text-white">
                {distribution?.queries_used?.toLocaleString() || 0}
                <span className="text-sm text-slate-400 ml-1">
                  ({Math.round((distribution?.queries_used / distribution?.total_queries) * 100) || 0}%)
                </span>
              </div>
            </div>
          </div>
          
          {distribution?.distribution_note && (
            <div className="mt-4 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <p className="text-sm text-indigo-300 flex items-center gap-2">
                <Info className="w-4 h-4" />
                {distribution.distribution_note}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Query Add-on Packages */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-400" />
            Purchase Query Add-ons
          </CardTitle>
          <CardDescription className="text-slate-400">
            Need more queries this month? Purchase add-on packages instantly
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {addons.map((addon) => (
              <div 
                key={addon.id}
                className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-amber-500/50 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-white">{addon.label}</span>
                  {addon.contact_sales ? (
                    <Badge variant="outline" className="text-amber-400 border-amber-500/50">Contact Sales</Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-400">RM {addon.price}</Badge>
                  )}
                </div>
                {!addon.contact_sales && (
                  <p className="text-sm text-slate-400 mb-4">
                    Add {addon.queries.toLocaleString()} AI queries to your monthly quota
                  </p>
                )}
                <Button
                  onClick={() => handlePurchaseAddon(addon.id)}
                  disabled={purchasing === addon.id}
                  className={addon.contact_sales 
                    ? "w-full bg-slate-700 hover:bg-slate-600" 
                    : "w-full bg-amber-600 hover:bg-amber-700"
                  }
                  data-testid={`purchase-addon-${addon.id}`}
                >
                  {purchasing === addon.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : addon.contact_sales ? (
                    'Contact Sales'
                  ) : (
                    <>
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Purchase Now
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Department Quota Management */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-cyan-400" />
                Department Query Quotas
              </CardTitle>
              <CardDescription className="text-slate-400">
                Allocate query quotas to different departments based on their needs
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge 
                variant={isOverBudget ? "destructive" : "secondary"}
                className={isOverBudget ? "" : "bg-emerald-500/20 text-emerald-400"}
              >
                {totalPercentage.toFixed(1)}% / 100%
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Total allocation bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Total Allocation</span>
              <span className={isOverBudget ? "text-red-400" : "text-emerald-400"}>
                {isOverBudget ? 'Over budget!' : `${(100 - totalPercentage).toFixed(1)}% unallocated`}
              </span>
            </div>
            <Progress 
              value={Math.min(totalPercentage, 100)} 
              className={`h-3 ${isOverBudget ? '[&>div]:bg-red-500' : '[&>div]:bg-emerald-500'}`}
            />
          </div>
          
          {/* Department sliders */}
          <div className="space-y-6">
            {departmentQuotas.map((quota) => {
              const currentValue = editedQuotas[quota.department] ?? quota.quota_percentage;
              const monthlyLimit = distribution?.total_queries 
                ? Math.round(distribution.total_queries * currentValue / 100)
                : 0;
              const usagePercent = quota.monthly_limit > 0 
                ? (quota.used / quota.monthly_limit * 100) 
                : 0;
              
              return (
                <div key={quota.department} className="p-4 rounded-lg bg-slate-800/30 border border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-slate-400" />
                      <span className="font-medium text-white">{quota.department}</span>
                      {quota.has_custom_quota && (
                        <Badge variant="outline" className="text-xs">Custom</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-slate-400">
                        {quota.used.toLocaleString()} / {monthlyLimit.toLocaleString()} used
                      </span>
                      <Badge 
                        className={
                          usagePercent >= 100 ? "bg-red-500/20 text-red-400" :
                          usagePercent >= 80 ? "bg-amber-500/20 text-amber-400" :
                          "bg-emerald-500/20 text-emerald-400"
                        }
                      >
                        {currentValue.toFixed(0)}%
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[currentValue]}
                      onValueChange={([val]) => handleQuotaChange(quota.department, val)}
                      max={100}
                      step={1}
                      className="flex-1"
                    />
                    <input
                      type="number"
                      value={currentValue}
                      onChange={(e) => handleQuotaChange(quota.department, Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                      className="w-16 px-2 py-1 text-center text-white bg-slate-700 border border-slate-600 rounded"
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  {/* Department usage bar */}
                  <div className="mt-3">
                    <Progress 
                      value={Math.min(usagePercent, 100)} 
                      className={`h-1.5 ${
                        usagePercent >= 100 ? '[&>div]:bg-red-500' :
                        usagePercent >= 80 ? '[&>div]:bg-amber-500' :
                        '[&>div]:bg-cyan-500'
                      }`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700">
            <Button 
              variant="outline" 
              onClick={handleResetQuotas}
              disabled={saving}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button 
              onClick={handleSaveQuotas}
              disabled={saving || isOverBudget}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-quotas-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Quotas
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Purchase History */}
      {purchases.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <History className="w-5 h-5 text-slate-400" />
              Add-on Purchase History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {purchases.map((purchase) => (
                <div 
                  key={purchase.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                >
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-amber-400" />
                    <div>
                      <div className="text-white font-medium">
                        +{purchase.queries_added.toLocaleString()} queries
                      </div>
                      <div className="text-sm text-slate-400">
                        {new Date(purchase.created_at).toLocaleDateString('en-MY', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-medium">
                      RM {purchase.price_paid.toFixed(2)}
                    </div>
                    <Badge 
                      className={
                        purchase.payment_status === 'paid' 
                          ? "bg-emerald-500/20 text-emerald-400" 
                          : "bg-amber-500/20 text-amber-400"
                      }
                    >
                      {purchase.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Info Note */}
      <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-indigo-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-400">
            <p className="font-medium text-slate-300 mb-1">How Query Distribution Works</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Queries are shared across your entire team from a monthly pool</li>
              <li>When you have fewer active users than your plan allows, each user gets more queries</li>
              <li>Department quotas let you reserve portions of queries for specific teams</li>
              <li>Add-on purchases are added to your monthly pool immediately</li>
              <li>Unused add-on queries do not roll over to the next month</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryManagementPage;
